import React, { useCallback, useReducer } from 'react';

export type Portal = {
	variant: string;
	priority: number;
	id?: string;
};

export type PortalsContextValue = {
	activePortals: Portal[];
	registerPortal?: (variant: string, priority: number) => Portal;
	unregisterPortal: (variant: string, portalId: string | null) => void;
	canRenderPortal: (variant: string, portalId: string | null) => boolean;
};

const INITIAL_STATE: PortalsContextValue = {
	activePortals: [],
	canRenderPortal: () => false,
	unregisterPortal: () => {},
};

const PortalsContext = React.createContext(INITIAL_STATE);
PortalsContext.displayName = 'PortalsContext';

interface Props {
	children: React.ReactNode;
}

type PortalContextState = {
	index: number;
	portals: { [key: string]: Portal[] };
};

const REGISTER_PORTAL = 'REGISTER_PORTAL';
const UNREGISTER_PORTAL = 'UNREGISTER_PORTAL';

type PortalContextAction =
	| { type: typeof REGISTER_PORTAL; payload: { variant: string; priority: number; id: string } }
	| { type: typeof UNREGISTER_PORTAL; payload: { variant: string; portalId: string | null } };

const registeredPortalsReducer: React.Reducer<PortalContextState, PortalContextAction> = (
	state: PortalContextState,
	action: PortalContextAction,
) => {
	switch (action.type) {
		case REGISTER_PORTAL: {
			const { variant, priority, id } = action.payload;
			const portalsOfVariant = state.portals[variant];
			const portal = {
				variant,
				priority,
				id,
			};

			return {
				...state,
				portals: {
					...state.portals,
					[variant]: portalsOfVariant ? [...portalsOfVariant, portal] : [portal],
				},
				index: state.index + 1,
			};
		}
		case UNREGISTER_PORTAL: {
			const { variant, portalId } = action.payload;
			const portalsOfVariant = state.portals[variant];

			return portalsOfVariant
				? {
						...state,
						portals: {
							...state.portals,
							[variant]: portalsOfVariant.filter((p) => p.id !== portalId),
						},
				  }
				: state;
		}
		default:
			return state;
	}
};

export const PortalsProvider = (props: Props) => {
	const [state, dispatch] = useReducer(registeredPortalsReducer, {
		portals: {},
		index: 0,
	});

	const unregisterPortal = useCallback((variant: string, portalId: string | null) => {
		dispatch({
			type: UNREGISTER_PORTAL,
			payload: {
				variant,
				portalId,
			},
		});
	}, []);

	const registerPortal = useCallback(
		(variant: string, priority: number) => {
			const id = Math.floor(Math.random() * 1000000000) + '__' + variant + '__' + state.index;
			const payload = {
				id,
				variant,
				priority,
			};
			dispatch({
				type: REGISTER_PORTAL,
				payload,
			});

			return payload;
		},
		[state.index],
	);

	const canRenderPortal = (variant: string, portalId: string | null) => {
		if (!portalId) return false;
		const portals = state.portals[variant];
		const idx = portals.findIndex((p) => p.id === portalId);
		if (idx === -1) return false;

		const portal = portals[idx];

		return portals.every((p, index) => {
			if (p.priority > portal.priority || (p.priority === portal.priority && index < idx)) {
				return false;
			}

			return true;
		});
	};

	const value: PortalsContextValue = {
		activePortals: [],
		registerPortal,
		unregisterPortal,
		canRenderPortal,
	};
	return <PortalsContext.Provider value={value}>{props.children}</PortalsContext.Provider>;
};

export default PortalsContext;
